import { useMemo } from 'react';

import { LeadsTableTab } from '@ekhodealer/ekho-common';

import type { LeadsFacets, LeadsTableRow,PaginatedResponse } from '@ekhodealer/ekho-common';

import { PLACEHOLDER } from '@common/lib/strings';

import { useSearchLeads } from '../api/useSearchLeads';

/**
 * Hook to fetch and calculate lead counts for each tab
 * Returns a function to get the count for a specific tab as a string
 * PLACEHOLDER is returned if query is pending, a number string for actual count, and '0' only if result is actually 0
 */
export function useLeadsTableTabCounts(): (tab: LeadsTableTab) => string {
	// Fetch lead counts for all tabs using facets (no filters applied for tab counts)
	// This ensures tab counts remain consistent regardless of search or other filters
	const { data: leadsData, isPending } = useSearchLeads({
		filters: {}, // No filters - we want the raw counts
		facets: ['pipeline'],
		limit: 0, // We only need facets, not the data
	});

	// Calculate counts for each tab using transformed facets
	const getTabCount = useMemo(() => {
		return (tab: LeadsTableTab): string => {
			// If query is pending/loading, show PLACEHOLDER
			if (isPending) {
				return PLACEHOLDER;
			}

			const response = leadsData as PaginatedResponse<LeadsTableRow> | undefined;
			const facets = response?.facets as LeadsFacets | undefined;
			const pipelineCounts: Partial<Record<LeadsTableTab, number>> =
				facets?.pipelineCounts || {};
			const totalCount = response?.count ?? 0;

			// For ALL tab, use the total count from the response
			if (tab === LeadsTableTab.ALL) {
				return totalCount.toLocaleString();
			}

			// For other tabs, use the facet counts
			return (pipelineCounts[tab] ?? 0).toLocaleString();
		};
	}, [leadsData, isPending]);

	return getTabCount;
}
